import React, { useCallback, useEffect, useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { useNavigate } from 'react-router-dom';
import {
  Content,
  ContentHeader,
  Header,
  HeaderLabel,
  Page,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { useApi, discoveryApiRef, identityApiRef, configApiRef } from '@backstage/frontend-plugin-api';
import { scmAuthApiRef } from '@backstage/integration-react';
import {
  Button,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { awsCredentialsApiRef } from '@eng-portal/czi-extensions-react';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder-react';
import { REQUEST_TYPE_LABELS, includesAdminGroup } from '@eng-portal/czi-extensions-common';
import { InfraRequestsClient, InfraRequest } from './client';
import { FULFILLMENT_CONFIGS } from './fulfillment';
import { InputRenderer } from './InputRenderers';

const REQUEST_TEMPLATE_MAP: Record<string, string> = {
  'aws-account': 'request-new-aws-account',
};

const STATUS_COLORS: Record<string, 'default' | 'primary' | 'secondary'> = {
  pending: 'default',
  approved: 'primary',
  creating: 'primary',
  created: 'primary',
  rejected: 'secondary',
  failed: 'secondary',
};

function StatusChip({ status }: { status: string }): JSX.Element {
  return <Chip label={status} color={STATUS_COLORS[status] ?? 'default'} size="small" />;
}

function KindChip({ kind }: { kind: string }): JSX.Element {
  const label = REQUEST_TYPE_LABELS[kind] ?? kind;
  return <Chip label={label} variant="outlined" size="small" />;
}

export function InfraRequestsPage(): JSX.Element {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const awsCredentialsApi = useApi(awsCredentialsApiRef);
  const scmAuth = useApi(scmAuthApiRef);
  const scaffolderApi = useApi(scaffolderApiRef);
  const config = useApi(configApiRef);

  const navigate = useNavigate();
  const client = new InfraRequestsClient(discoveryApi, identityApi);

  const [error, setError] = useState<string | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approveTarget, setApproveTarget] = useState<InfraRequest | null>(null);
  const [detailDialog, setDetailDialog] = useState<InfraRequest | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentUserRef, setCurrentUserRef] = useState<string>('');
  const [isAdmin, setIsAdmin] = useState(false);

  useAsync(async () => {
    const identity = await identityApi.getBackstageIdentity();
    setCurrentUserRef(identity.userEntityRef);
    setIsAdmin(includesAdminGroup(identity.ownershipEntityRefs, { config }));
  }, [identityApi, config]);

  const { value: requests, loading } = useAsync(
    () => client.list(),
    [refreshKey],
  );

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  // Auto-reconcile: check scaffolder task status for in-flight requests
  // and update to created/failed automatically.
  useEffect(() => {
    if (!requests || !isAdmin) return;
    const inFlight = requests.filter(
      r => (r.status === 'approved' || r.status === 'creating') && r.scaffolder_task_id,
    );
    if (inFlight.length === 0) return;

    let didUpdate = false;
    (async (): Promise<void> => {
      for (const req of inFlight) {
        try {
          const task = await scaffolderApi.getTask(req.scaffolder_task_id!);
          const taskStatus = (task as any)?.status;
          if (taskStatus === 'completed') {
            await client.updateStatus(req.id, 'created');
            didUpdate = true;
          } else if (taskStatus === 'failed' || taskStatus === 'cancelled') {
            await client.updateStatus(req.id, 'failed');
            didUpdate = true;
          }
        } catch {
          // Task check failed — skip silently
        }
      }
      if (didUpdate) refresh();
    })();
  }, [requests, isAdmin, scaffolderApi, client, refresh]);

  const handleApprove = useCallback(async (request: InfraRequest) => {
    try {
      setError(null);

      const config = FULFILLMENT_CONFIGS[request.kind];
      if (!config) {
        setError(`No fulfillment config for request type "${request.kind}"`);
        return;
      }

      const apis = { awsCredentials: awsCredentialsApi, scmAuth };
      const values = await config.getValues(request.inputs, apis);
      const secrets = await config.getSecrets(apis);

      const { taskId } = await scaffolderApi.scaffold({
        templateRef: config.templateRef,
        values: { ...values, infraRequestId: request.id },
        secrets,
      });

      await client.decide(request.id, 'approve', { taskId });
      await client.updateStatus(request.id, 'creating');

      navigate(`/create/tasks/${taskId}`);
    } catch (err: any) {
      setError(`Approve failed: ${err.message}`);
    }
  }, [awsCredentialsApi, scmAuth, scaffolderApi, client]);

  const handleRejectConfirm = useCallback(async () => {
    if (!rejectTarget) return;
    try {
      setError(null);
      await client.decide(rejectTarget, 'reject', { reason: rejectReason || undefined });
      setRejectDialogOpen(false);
      setRejectReason('');
      setRejectTarget(null);
      refresh();
    } catch (err: any) {
      setError(`Reject failed: ${err.message}`);
    }
  }, [rejectTarget, rejectReason, client, refresh]);

  const [approving, setApproving] = useState(false);

  const handleApproveConfirm = useCallback(async () => {
    if (!approveTarget) return;
    setApproving(true);
    try {
      await handleApprove(approveTarget);
    } catch {
      // Error already set by handleApprove
      setApproving(false);
      setApproveTarget(null);
    }
  }, [approveTarget, handleApprove]);

  const handleEdit = useCallback((request: InfraRequest) => {
    const templateName = REQUEST_TEMPLATE_MAP[request.kind];
    if (!templateName) {
      setError(`No request template for kind "${request.kind}"`);
      return;
    }
    const formData = {
      requestId: request.id,
      ...request.inputs,
    };
    const encoded = encodeURIComponent(JSON.stringify(formData));
    navigate(`/create/templates/default/${templateName}?formData=${encoded}`);
  }, []);

  const [newRequestAnchor, setNewRequestAnchor] = useState<null | HTMLElement>(null);

  const handleNewRequest = useCallback((event: React.MouseEvent<HTMLElement>) => {
    const kinds = Object.keys(REQUEST_TEMPLATE_MAP);
    if (kinds.length === 1) {
      // Short-circuit: only one type, navigate directly
      navigate(`/create/templates/default/${REQUEST_TEMPLATE_MAP[kinds[0]]}`);
    } else {
      setNewRequestAnchor(event.currentTarget);
    }
  }, [navigate]);

  const handleNewRequestSelect = useCallback((kind: string) => {
    setNewRequestAnchor(null);
    const templateName = REQUEST_TEMPLATE_MAP[kind];
    if (templateName) {
      navigate(`/create/templates/default/${templateName}`);
    }
  }, [navigate]);

  const columns: TableColumn<InfraRequest>[] = [
    {
      title: 'Type',
      render: row => <KindChip kind={row.kind} />,
    },
    { title: 'Name', render: row => row.inputs.accountName ?? row.inputs.name ?? row.id },
    {
      title: 'Status',
      render: row => <StatusChip status={row.status} />,
    },
    { title: 'Requester', field: 'requester_ref' },
    {
      title: 'Created',
      render: row => new Date(row.created_at).toLocaleDateString(),
    },
    {
      title: 'Actions',
      render: row => (
        <>
          <Button
            size="small"
            color="default"
            onClick={() => setDetailDialog(row)}
            style={{ marginRight: 4 }}
          >
            Details
          </Button>
          {row.status === 'pending' && (
            <>
              {(isAdmin || currentUserRef === row.requester_ref) && (
                <Button
                  size="small"
                  color="default"
                  onClick={() => handleEdit(row)}
                  style={{ marginRight: 4 }}
                >
                  Edit
                </Button>
              )}
              {isAdmin && (
                <Button
                  size="small"
                  color="primary"
                  variant="contained"
                  onClick={() => setApproveTarget(row)}
                  style={{ marginRight: 4 }}
                >
                  Approve
                </Button>
              )}
              {(isAdmin || currentUserRef === row.requester_ref) && (
                <Button
                  size="small"
                  color="secondary"
                  onClick={() => {
                    setRejectTarget(row.id);
                    setRejectDialogOpen(true);
                  }}
                >
                  {currentUserRef === row.requester_ref && !isAdmin ? 'Withdraw' : 'Reject'}
                </Button>
              )}
            </>
          )}
          {row.scaffolder_task_id && (
            <Button
              size="small"
              color="primary"
              onClick={() => navigate(`/create/tasks/${row.scaffolder_task_id}`)}
            >
              View Task
            </Button>
          )}
          {isAdmin && row.status === 'approved' && (
            <Button
              size="small"
              color="secondary"
              onClick={async () => {
                try {
                  setError(null);
                  await client.updateStatus(row.id, 'failed');
                  refresh();
                } catch (err: any) {
                  setError(`Mark failed: ${err.message}`);
                }
              }}
              style={{ marginLeft: 4 }}
            >
              Mark Failed
            </Button>
          )}
          {(isAdmin || currentUserRef === row.requester_ref) && row.status === 'failed' && (
            <>
              <Button
                size="small"
                color="default"
                onClick={() => handleEdit(row)}
                style={{ marginLeft: 4 }}
              >
                Edit
              </Button>
              {isAdmin && (
              <Button
                size="small"
                color="primary"
                variant="contained"
                onClick={() => setApproveTarget(row)}
                style={{ marginLeft: 4 }}
              >
                Retry
              </Button>
              )}
            </>
          )}
        </>
      ),
    },
  ];

  return (
    <Page themeId="tool">
      <Header title="Infra Requests" subtitle="Review and approve infrastructure provisioning requests">
        <HeaderLabel label="Owner" value="Central Infra" />
      </Header>
      <Content>
        <ContentHeader title="All Requests">
          <Button
            variant="contained"
            color="primary"
            onClick={handleNewRequest}
            style={{ marginRight: 8 }}
          >
            New Request
          </Button>
          <Menu
            anchorEl={newRequestAnchor}
            open={!!newRequestAnchor}
            onClose={() => setNewRequestAnchor(null)}
          >
            {Object.entries(REQUEST_TEMPLATE_MAP).map(([kind]) => (
              <MenuItem key={kind} onClick={() => handleNewRequestSelect(kind)}>
                {REQUEST_TYPE_LABELS[kind] ?? kind}
              </MenuItem>
            ))}
          </Menu>
          <Button variant="outlined" onClick={refresh}>Refresh</Button>
        </ContentHeader>
        {error && <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>}
        <Table<InfraRequest>
          columns={columns}
          data={requests ?? []}
          isLoading={loading}
          title="Requests"
          options={{ paging: true, pageSize: 20 }}
        />

        {/* Approve confirmation dialog */}
        <Dialog open={!!approveTarget} onClose={() => setApproveTarget(null)}>
          <DialogTitle>Approve Request</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {approving
                ? 'Minting credentials and triggering account creation...'
                : 'This will mint AWS credentials and trigger the account creation '
                  + 'automation. Are you sure you want to approve this request?'
              }
            </DialogContentText>
            {approving && (
              <Box display="flex" justifyContent="center" mt={2}>
                <CircularProgress />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setApproveTarget(null); setApproving(false); }} disabled={approving}>
              Cancel
            </Button>
            <Button onClick={handleApproveConfirm} color="primary" variant="contained" disabled={approving}>
              {approving ? 'Approving...' : 'Confirm Approve'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reject dialog */}
        <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
          <DialogTitle>Reject Request</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Optionally provide a reason for rejection.
            </DialogContentText>
            <TextField
              autoFocus
              fullWidth
              label="Reason"
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleRejectConfirm} color="secondary">Reject</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={!!detailDialog}
          onClose={() => setDetailDialog(null)}
          maxWidth="md"
          fullWidth
        >
          {detailDialog && (
            <>
              <DialogTitle>
                Request Details — {REQUEST_TYPE_LABELS[detailDialog.kind] ?? detailDialog.kind}
              </DialogTitle>
              <DialogContent>
                {(detailDialog.status === 'rejected' || detailDialog.status === 'approved') && (
                  <Box mb={2} p={1.5} style={{
                    backgroundColor: detailDialog.status === 'rejected' ? '#fff3f3' : '#f3fff3',
                    borderRadius: 4,
                  }}>
                    <Typography variant="subtitle2">
                      {REQUEST_TYPE_LABELS[detailDialog.kind] ?? detailDialog.kind}
                      {' request '}
                      {detailDialog.status === 'rejected' ? 'rejected' : 'approved'}
                      {detailDialog.approver_ref && ` by ${detailDialog.approver_ref}`}
                      {detailDialog.decided_at && ` on ${new Date(detailDialog.decided_at).toLocaleDateString()}`}
                    </Typography>
                    {detailDialog.reason && (
                      <Typography variant="body2" style={{ marginTop: 4 }}>
                        Reason: {detailDialog.reason}
                      </Typography>
                    )}
                  </Box>
                )}
                <InputRenderer kind={detailDialog.kind} inputs={detailDialog.inputs} />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDetailDialog(null)}>Close</Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Content>
    </Page>
  );
}
